import { useEffect, useState, useCallback } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLayout } from "./integration/useLayoutAdapter";
import {
  getAllVoiceProfiles,
  saveVoiceProfile,
  deleteVoiceProfile,
  ensureStarterModes,
} from "./utils/voiceProfileDB";
import ResizablePanel from "./components/common/ResizablePanel";
import VoiceProfileListPanel from "./panels/VoiceProfileListPanel";
import VoiceProfileDetailPanel from "./panels/VoiceProfileDetailPanel";
import EnrichmentRulesListPanel from "./panels/EnrichmentRulesListPanel";
import EnrichmentRulesDetailPanel from "./panels/EnrichmentRulesDetailPanel";
import { ENRICHMENT_RULES } from "./data/enrichmentRules";

let _idCounter = 0;
function makeId() {
  return `mode_${Date.now().toString(36)}_${(++_idCounter).toString(36)}`;
}

const EMPTY_MODE = {
  name: "",
  formality: 3,
  contractions: "Sometimes",
  greeting: "Hi [name]",
  signoff: "Best",
  length: "short_paragraph",
  emojis: "Never",
  toneArchetype: "direct_warm",
  voiceSample: "",
  defaultPrompt: "",
  isStarter: false,
};

const PageMyMagic = ({ onSaveCompanyKnowledge }) => {
  const { isPanelOpen, togglePanel, setPanels } = useLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeModeType, setActiveModeType] = useState("writing");

  const loadProfiles = useCallback(async (modeType) => {
    setLoading(true);
    await ensureStarterModes(modeType);
    const all = await getAllVoiceProfiles(modeType);
    setProfiles(all);
    setSelected((prev) => (prev && all.some((p) => p.id === prev.id) ? prev : null));
    setLoading(false);
  }, []);

  useEffect(() => {
    loadProfiles(activeModeType);
  }, [loadProfiles, activeModeType]);

  const handleAdd = () => {
    const newMode = { ...EMPTY_MODE, id: makeId(), modeType: activeModeType };
    setSelected(newMode);
  };

  const handleSave = async (profile) => {
    const saved = await saveVoiceProfile({ ...profile, modeType: activeModeType });
    setProfiles((prev) => {
      const exists = prev.find((p) => p.id === saved.id);
      if (exists) return prev.map((p) => (p.id === saved.id ? saved : p));
      return [...prev, saved];
    });
    setSelected(saved);
  };

  const handleDelete = async (profile) => {
    await deleteVoiceProfile(profile.id);
    setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
    if (selected?.id === profile.id) setSelected(null);
  };

  const handleEdit = (profile) => {
    setSelected(profile);
  };

  const voiceListOpen = isPanelOpen("voiceProfiles");
  const textListOpen = isPanelOpen("textProfiles");
  const voiceDetailOpen = isPanelOpen("voiceProfileDetail");
  const rulesListOpen = isPanelOpen("enrichmentRules");
  const rulesDetailOpen = isPanelOpen("enrichmentRulesDetail");
  const [selectedRule, setSelectedRule] = useState(ENRICHMENT_RULES[0]);

  // My Magic modes are mutually exclusive and always-on in this menu.
  useEffect(() => {
    if (voiceListOpen || textListOpen || voiceDetailOpen || rulesListOpen) return;
    setPanels({
      voiceProfiles: true,
      textProfiles: false,
      voiceProfileDetail: false,
      enrichmentRules: false,
      enrichmentRulesDetail: false,
    });
  }, [voiceListOpen, textListOpen, voiceDetailOpen, rulesListOpen, setPanels]);

  useEffect(() => {
    if (textListOpen) setActiveModeType("text");
    else if (voiceListOpen) setActiveModeType("writing");
  }, [voiceListOpen, textListOpen]);

  const handleSelectRule = (rule) => {
    setSelectedRule(rule);
    if (!rulesDetailOpen) togglePanel("enrichmentRulesDetail");
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      height: "100%",
      gap: isMobile ? 0 : 1,
      ...(isMobile ? { mx: -1.5, mt: -2, mb: -2, width: "calc(100% + 24px)", height: "calc(100% + 32px)" } : {}),
    }}>
      {(voiceListOpen || textListOpen) && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 420}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 560}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <VoiceProfileListPanel
            loading={loading}
            profiles={profiles}
            modeKind={activeModeType}
            selectedId={selected?.id}
            onSelect={handleEdit}
            onAdd={handleAdd}
            onDelete={handleDelete}
            onSaveCompanyKnowledge={onSaveCompanyKnowledge}
            onClose={() => {}}
          />
        </ResizablePanel>
      )}

      {voiceDetailOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 700}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <VoiceProfileDetailPanel
            profile={selected}
            modeKind={activeModeType}
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => {}}
          />
        </ResizablePanel>
      )}

      {rulesListOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 360}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 480}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <EnrichmentRulesListPanel
            selectedId={selectedRule?.id}
            onSelect={handleSelectRule}
            onClose={() => {}}
          />
        </ResizablePanel>
      )}

      {rulesDetailOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 720}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <EnrichmentRulesDetailPanel
            rule={selectedRule}
            onClose={() => {}}
          />
        </ResizablePanel>
      )}
    </Box>
  );
};

export default PageMyMagic;
